import { useEffect } from "react";
import {
  capturedOptroReferrer,
  claimOptroAutoSignInAttempt,
} from "../lib/optroReferrer";
import { startOptroLogin } from "../lib/optroLogin";

/**
 * Silently bounces straight into the Optro sign-in flow when this tab arrived
 * from a known Optro/AuditBoard domain (e.g. the Optro "Set Up Automated Test"
 * launch), instead of requiring the user to click "Sign in with Optro" and
 * re-enter their instance.
 *
 * The instance comes from `capturedOptroReferrer()`, recorded at boot — see
 * `optroReferrer.ts` for why it cannot be read from `document.referrer` here.
 *
 * Not gated on the `sign-in-with-optro` flag. On this anonymous page that flag
 * only ever evaluates the anonymous LaunchDarkly context, which is off in
 * regions that do use Optro sign-in, so gating on it stranded exactly the
 * users arriving from Optro. Arriving from an Optro instance is the signal;
 * the backend's `_ALLOWED_OPTRO_BASE_DOMAINS` allowlist in `optro_oauth.py`
 * still bounds which hosts a kickoff can target.
 *
 * Fires at most once per arrival from Optro until an Optro sign-in succeeds
 * (`OptroCallback` resets it). A failed sign-in routes back to `/signin`
 * with the capture still present, so without the guard it would bounce
 * straight back into the same failure. A `startOptroLogin` failure (e.g. a
 * non-secure-context origin) is swallowed rather than surfaced — this is a
 * convenience bounce, not a user-initiated action, so it falls through to the
 * manual button.
 */
export function useOptroReferrerAutoSignIn(): void {
  useEffect(() => {
    const baseUrl = capturedOptroReferrer();
    if (!baseUrl) return;
    if (!claimOptroAutoSignInAttempt()) return;

    startOptroLogin(baseUrl).catch((err: unknown) => {
      console.error("Optro referrer auto sign-in failed", err);
    });
  }, []);
}
